/**
 * Уведомления о входящих сообщениях.
 *
 * Раньше звук и вибрация жили прямо в хуке веб-сокета, а хук монтировался
 * в трёх местах (App, список чатов, открытый чат) — на одно сообщение
 * прилетало два-три звука. Плюс звук глохнул на вложениях без текста, а
 * переключатель «Push-уведомления» только спрашивал разрешение и ничего
 * не показывал.
 *
 * Здесь один обработчик на всё приложение: он сам решает, надо ли шуметь,
 * и показывает системное уведомление, если пользователь включил push.
 */

import { Capacitor } from '@capacitor/core';
import { LocalNotifications } from '@capacitor/local-notifications';
import { readNotificationPrefs } from '../components/Settings/notificationPrefs';
import {
    getMessageConversationId,
    getMessageText,
    isIncomingMessage,
    messageEventKey,
    WsPayload,
} from '../api/wsMessage';

/** Событие клика по уведомлению: открыть указанную беседу. */
export const OPEN_CHAT_EVENT = 'orion:open-chat';

/** Сколько последних сообщений помнить для защиты от повторных уведомлений. */
const MAX_REMEMBERED_MESSAGES = 100;

/** Уже показанные сообщения: одно сообщение — один звук и одно уведомление. */
const shownMessages: string[] = [];

/** Общий аудиоэлемент: браузер разрешает воспроизведение после первого клика. */
let audio: HTMLAudioElement | null = null;

const getAudio = (): HTMLAudioElement | null => {
    if (typeof Audio === 'undefined') return null;
    if (!audio) audio = new Audio('/assets/sounds/notification.mp3');
    return audio;
};

/** Помнит сообщение, если о нём ещё не уведомляли. */
const rememberMessage = (key: string): boolean => {
    if (shownMessages.includes(key)) return false;
    shownMessages.push(key);
    if (shownMessages.length > MAX_REMEMBERED_MESSAGES) shownMessages.shift();
    return true;
};

/** Текст системного уведомления: у вложения текста нет. */
const notificationBody = (message: WsPayload): string => {
    const text = getMessageText(message).trim();
    return text || 'Вложение';
};

/** Открыть беседу по клику на уведомление (или по тапу на нативном пуше). */
export const openChat = (conversationId: string) => {
    window.dispatchEvent(
        new CustomEvent(OPEN_CHAT_EVENT, { detail: { conversationId } })
    );
};

/**
 * Показать системное уведомление. В браузере — только когда вкладка не на
 * экране: иначе уведомление дублирует и без того видимый чат.
 */
const showNotification = (message: WsPayload, conversationId: string) => {
    const conversationKey = Number(conversationId.replace(/\D/g, '').slice(0, 9));

    if (Capacitor.isNativePlatform()) {
        LocalNotifications.schedule({
            notifications: [
                {
                    id: Number.isFinite(conversationKey) ? conversationKey : 1,
                    title: message.senderName || 'Новое сообщение',
                    body: notificationBody(message),
                    extra: { conversationId },
                },
            ],
        }).catch(() => {});
        return;
    }

    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
    if (document.visibilityState === 'visible') return;

    try {
        const notification = new Notification(message.senderName || 'Новое сообщение', {
            body: notificationBody(message),
            tag: conversationId,
        });
        notification.onclick = () => {
            window.focus();
            openChat(conversationId);
            notification.close();
        };
    } catch (error) {
        // Без разрешения или в неподдерживающем окружении — просто без пуша.
        console.warn('Push notification error:', error);
    }
};

/**
 * Обработать входящее событие сокета: проиграть звук, вибрацию и, если
 * пользователь включил push, показать уведомление.
 *
 * Вызывается ровно один раз на сообщение — из общего соединения,
 * поэтому дубли исключены даже при нескольких смонтированных компонентах.
 */
export const alertIncomingMessage = (
    message: WsPayload | null | undefined,
    currentUserId?: string | number | null
) => {
    if (!message) return;

    // Статусы «онлайн/офлайн» и «печатает» — не сообщения.
    if (message.event) return;

    const conversationId = getMessageConversationId(message);
    if (!conversationId) return;

    if (!isIncomingMessage(message, currentUserId)) return;

    const key = messageEventKey(message);
    if (!key || !rememberMessage(key)) return;

    const prefs = readNotificationPrefs();

    if (prefs.sound) getAudio()?.play().catch(() => {});
    if (prefs.vibration && typeof navigator !== 'undefined' && 'vibrate' in navigator) {
        navigator.vibrate(200);
    }
    if (prefs.push) showNotification(message, conversationId);
};
